import { Text, type TextProps } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import { type CoreItem, type CoreItemPricingTier } from '@flecto/types'
import { TextCurrency } from '@flecto/ui'

import { usePricingTierLabel } from '../../../booking-products/hooks/usePricingTierLabel'
import { useTaxesInfo } from '../../../taxes'
import { useItemLowestPriceRule } from '../../hooks/useItemLowestPriceRule'

export type TextItemPriceProps = TextProps & {
  item: CoreItem
  fallbackText?: string
}

export function TextItemPrice({
  item,
  fallbackText,
  ...props
}: TextItemPriceProps) {
  const { t: tItems } = useTranslation('saas', { keyPrefix: 'items' })
  const rule = useItemLowestPriceRule({ item })
  const { getCalculatedAmounts, userSettingShowPricesWithTaxes } =
    useTaxesInfo()

  if (!rule) {
    return fallbackText ? <Text {...props}>{fallbackText}</Text> : null
  }

  const amounts = getCalculatedAmounts(
    rule.price_cents,
    item.tax_rate,
    item.taxable
  )

  const price = userSettingShowPricesWithTaxes
    ? amounts.totalWithTaxes.cents
    : amounts.totalWithoutTaxes.cents

  const itemLabel = `${usePricingTierLabel({
    itemTier: rule as CoreItemPricingTier,
  })}`

  const translateLabel = (label: string) => {
    const regex = /\((\d+) (day|days|hour|hours)\)/
    const match = label.match(regex)
    if (match) {
      const [, number, unit] = match
      const translatedUnit = tItems(`overtime_charge.${unit}`)
      return `(${number} ${translatedUnit})`
    }
  }

  return (
    <Text data-testid='text-item-price' {...props}>
      <TextCurrency
        cents={price}
        size={props.size}
        c={props.color}
        fw={props.fw}
        truncate={props.truncate}
        display='inline'
      />
      <Text
        size={props.size}
        c={props.color}
        fw={props.fw}
        truncate={props.truncate}
        tt='lowercase'
        display='inline'
        ml='xs'
      >
        {translateLabel(itemLabel)}
      </Text>
    </Text>
  )
}
